/** @jsx jsx */
import { jsx, Flex } from 'theme-ui'
import { useState, useEffect } from 'react'
import NumberInput from '../../elements/NumberInput/number-input'
import { CalculateLoanRepayment } from '../../../../helpers/mortgages-calculations'

const LoanRepaymentCalculator = (): JSX.Element => {
  const [infoPanel, showInfoPanel] = useState<boolean>(false)

  const [data, setData] = useState({
    amount: 0,
    term: 0,
    apr: 0,
  })

  const [result, setResult] = useState({
    monthlyPayment: 0,
    total: 0,
    totalinterest: 0,
  })

  const returnData = (key: string, returnValue: string) => {
    setData((prevData) => ({
      ...prevData,
      [key]: parseFloat(returnValue),
    }))
  }

  const toggleInfoPanel = () => {
    showInfoPanel(!infoPanel)
  }

  useEffect(() => {
    const calculateLoanRepayment = () => {
      setResult(CalculateLoanRepayment({ amount: data.amount, apr: data.apr, term: data.term }))
    }

    // here add calculation call and debounce it
    calculateLoanRepayment()
  }, [data])

  return (
    <div sx={{ backgroundColor: '#34454e', color: '#fff' }}>
      <Flex sx={{ padding: '20px', margin: '0 -7px', flexWrap: 'wrap' }}>
        <div
          sx={{ width: ['100%', '100%', '33.33333%'], padding: '0 7px', boxSizing: 'border-box' }}
        >
          <NumberInput
            returnInputValue={returnData}
            allowZero
            step={1000}
            minValue={1000}
            maxValue={100000}
            defaultValue={7500}
            inputName={'amount'}
            labelText={'I wish to borrow:'}
            inputValuePrefix='£'
            decimal={false}
            isCurrency={true}
          />
        </div>
        <div
          sx={{ width: ['100%', '50%', '33.33333%'], padding: '0 7px', boxSizing: 'border-box' }}
        >
          <NumberInput
            returnInputValue={returnData}
            allowZero
            labelText={'over:'}
            maxValue={25}
            defaultValue={5}
            minValue={1}
            step={1}
            inputName={'term'}
            inputValueSuffix=' years'
            decimal={false}
          />
        </div>
        <div
          sx={{ width: ['100%', '50%', '33.33333%'], padding: '0 7px', boxSizing: 'border-box' }}
        >
          <NumberInput
            returnInputValue={returnData}
            allowZero
            labelText={'at an APR of:'}
            inputName={'apr'}
            inputValueSuffix='%'
            maxValue={100}
            defaultValue={3.7}
            minValue={0}
            step={1}
            decimal={true}
          />
        </div>
      </Flex>
      <div>
        <p sx={{ fontSize: '.8em', color: '#b2b2b2', padding: ' 0 20px', textAlign: 'center' }}>
          The costs below are for illustrative purposes. Check the exact amounts with the lender
          when you decide on a loan.
        </p>
      </div>
      <div sx={{ display: 'flex', flexWrap: 'wrap', marginTop: ['5px'] }}>
        <div sx={InfoBoxLightGray}>
          <p sx={InfoBoxLabelStyles}>A monthly payment of: </p>
          <div sx={InfoBoxAmountStyles}>£{result ? result.monthlyPayment : ''}</div>
        </div>
        <div sx={InfoBoxDarkGray}>
          <p sx={InfoBoxLabelStyles}>Total cost of credit: </p>
          <div sx={InfoBoxAmountStyles}>£{result ? result.totalinterest : ''}</div>
        </div>
      </div>
      <div sx={{ display: 'flex', flexWrap: 'wrap', marginTop: ['0', '5px'] }}>
        <div sx={InfoBoxLightGray}>
          <p sx={InfoBoxLabelStyles}>based on an APR of:</p>
          <div sx={InfoBoxAmountStyles}>{data.apr}%</div>
        </div>
        <div sx={InfoBoxDarkGray}>
          <p sx={InfoBoxLabelStyles}>That&apos;s a total of:</p>
          <div sx={InfoBoxAmountStyles}>£{result ? result.total : ''}</div>
        </div>
      </div>
      <p
        sx={{
          marginTop: '15px',
          marginBottom: '0',
          fontWeight: '400',
          fontSize: '19px',
          textAlign: 'center',
        }}
      >
        Find a loan now
      </p>
      <Flex
        sx={{
          padding: '10px 10px 0',
          textAlign: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-evenly',
          alignItems: 'center',
        }}
      >
        <a sx={ButtonStyles} href='/loans/unsecured-loans'>
          Personal
        </a>
        <a sx={ButtonStyles} href='/loans/bad-credit-loans'>
          Bad Credit
        </a>
        <a sx={ButtonStyles} href='/loans/guarantor-loans'>
          Guarantor
        </a>
      </Flex>
      <div>
        <div
          sx={{
            backgroundColor: '#5d6a72',
            overflow: 'hidden',
            padding: ' 33px 33px 27px',
            marginTop: '22px',
            display: infoPanel ? 'block' : 'none',
          }}
        >
          <p sx={CalcTitleStyles}>Personal loans</p>
          <p sx={CalcTextStyles}>
            A personal loan is a type of unsecured loan, which means what you borrow isn&apos;t
            secured against any asset like your home. You borrow a fixed amount and pay it back plus
            interest over a set period.
          </p>
          <p sx={CalcTitleStyles}>Bad credit loans</p>
          <p sx={CalcTextStyles}>
            If you have a poor credit history, you could still borrow money with a bad credit loan.
            They can help you improve your credit rating if you keep up with repayments, but they
            usually come with a high APR.
          </p>
          <p sx={CalcTitleStyles}>Guarantor loans</p>
          <p sx={CalcTextStyles}>
            A guarantor loan works by appointing someone else, like a parent or friend, to make your
            loan repayments if you&apos;re unable to. You could get a guarantor loan if you have a
            poor credit record, or if you&apos;ve not borrowed before.
          </p>
        </div>
        <button
          sx={{
            display: 'block',
            width: '100%',
            marginBottom: '0',
            border: 'none',
            padding: '17px 0',
            textAlign: 'center',
            outline: 'none',
            boxShadow: 'none',
            background: 'transparent',
            textDecoration: 'underline',
            color: 'white',
            cursor: 'pointer',
          }}
          onClick={toggleInfoPanel}
        >
          <span>{!infoPanel ? "Unsure on which type of loan you're after?" : 'Close'}</span>{' '}
          <svg
            sx={{
              width: '12px',
              marginLeft: '7px',
              transform: infoPanel ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'all 0.2s linear',
            }}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 5.29 2.91'
          >
            <path
              stroke='#db4d75'
              fill='none'
              strokeWidth='.79'
              d='M.4.4l2.25 2.1L4.9.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            ></path>
          </svg>
        </button>
      </div>
    </div>
  )
}

const CalcTitleStyles = {
  fontWeight: '400',
  fontSize: '19px',
  marginBottom: '8px',
}

const CalcTextStyles = {
  fontWeight: 200,
  textAlign: 'left' as const,
  fontSize: '15px',
  lineHeight: '20px',
}

const ButtonStyles = {
  display: 'flex' as const,
  justifyContent: 'center' as const,
  alignItems: 'center',
  width: ['100%', '30%', '30%'],
  height: [46, 44],
  margin: ['3px 0', '3px 0', '0'],
  border: 'none',
  borderRadius: '4px',
  fontSize: '16px',
  textAlign: 'center' as const,
  lineHeight: '1em',
  color: '#fff',
  textDecoration: 'none' as const,
  backgroundColor: '#da4d76',
  outline: 'none' as const,
  cursor: 'pointer' as const,
}

const InfoBoxStyles = {
  width: ['100%', '50%'],
  flex: ['100% 0 0' as const, '50% 0 0' as const],
  height: '100%',
  padding: '10px 0',
  boxSizing: 'border-box' as const,
  textAlign: 'center' as const,
}

const InfoBoxLightGray = {
  ...InfoBoxStyles,
  backgroundColor: '#5d6a72',
}

const InfoBoxDarkGray = {
  ...InfoBoxStyles,
  backgroundColor: '#495860',
}

const InfoBoxLabelStyles = {
  color: '#b2b2b2',
  margin: '0',
  fontWeight: '400',
  fontSize: '13px',
}

const InfoBoxAmountStyles = {
  color: '#fff',
  fontWeight: '600',
  fontSize: '30px',
  margin: '-5px 0',
}

export default LoanRepaymentCalculator
